/**
 * PriorityStore
 * Manages priority levels and their descriptions
 */

import type { PriorityModel } from "@/models/PriorityModel";
import type { Ref } from "vue";

import { defineStore } from "pinia";
import { ref } from "vue";
import Api from "@/services/ApiService";
import Cache from "@/services/Cache";
import Events from "@/services/Emitter";

const priorityStore = () =>
{
    const CACHE_KEY = "store_priority_data";
    const list = ref<Record<number, PriorityModel>>({});
    const isInitialized = ref<boolean>(false);
    let initPromise: Promise<void> | null = null;

    const isEmpty = ():boolean =>
    {
        return Object.keys(list.value).length === 0;
    }

    const init = async (): Promise<void> => {
        // If already initialized, return immediately
        if (isInitialized.value) return;
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first
                const cached = await Cache.get(CACHE_KEY);
                if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
                    list.value = cached;
                    isInitialized.value = true;
                    return;
                }
                
                // Load from server if cache is empty
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };
    
    const reload = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, PriorityModel>>> =>
    {
        let response = await Api.loadPriorities(runInBackground);
        
        // Extract data array from ApiResponse
        let tmp = response.Data || [];
        let rec = {} as Record<number, PriorityModel>;
        
        for (let i:number = 0; i < tmp.length; i++)
        {
            if (tmp[i].Id) {
                rec[tmp[i].Id!] = tmp[i];
            }
        }
        
        list.value = rec;
        await Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
        return list;
    }

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const load = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, PriorityModel>>> =>
    {
        await init();
        return list;
    }
    
    const getById = async (priorityId:number):Promise<PriorityModel | null> =>
    {
        await init();
        
        if (list.value[priorityId]) {
            return list.value[priorityId];
        }
        
        return null;
    }

    const getLabel = async (priorityId:number):Promise<string> =>
    {
        const priority = await getById(priorityId);
        if (priority && priority.Level) {
            return priority.Level;
        }
        return priorityId.toString();
    }

    // register global events
    Events.on("PriorityStore::Reload", async () =>
    {
        console.debug("PriorityStore::Reload called...");
        await reload();
    });

    Events.on("refresh:priorityStore", async () => {
        console.debug("refresh:priorityStore event received");
        await refresh();
    });

    // Auto-initialize on store creation
    init();

    return { load, reload, refresh, getById, getLabel, list };
};

export const usePriorityStore = defineStore("priority", priorityStore);
